import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CARD_W = 280;
const CARD_H = 157;
const GAP = 14;
const AUTO_DELAY = 5000;
const EASE = 500;

const TAG_COLORS = {
  gold:   { bg: '#e8b84b', color: '#0a0a0f' },
  red:    { bg: '#ef4444', color: '#fff' },
  green:  { bg: '#22c55e', color: '#fff' },
  orange: { bg: '#f97316', color: '#fff' },
  purple: { bg: '#a855f7', color: '#fff' },
};

function getYTId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function ytEmbed(id) {
  // Always use loop enabled, control mute via postMessage
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0&iv_load_policy=3&enablejsapi=1&disablekb=1&showinfo=0&fs=0&cc_load_policy=0&playsinline=1`;
}

function ytThumb(id, customThumb) {
  // Only use custom thumbnail if it's an external URL (not local upload)
  if (customThumb && (customThumb.startsWith('http://') || customThumb.startsWith('https://'))) {
    return customThumb;
  }
  // Always use YouTube's default thumbnail for reliability
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

export default function FeaturedCarousel({ refreshTrigger }) {
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [containerW, setContainerW] = useState(0);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false); // Track if video is actually playing
  
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const iframeRef = useRef(null);
  const mutedRef = useRef(true);
  const hasPlayedOnceRef = useRef(false);

  // ── Swipe / Drag gesture tracking ──────────────────────────────
  const dragStartX = useRef(null);
  const isDragging = useRef(false);
  const SWIPE_THRESHOLD = 40; // px needed to trigger slide change

  const onDragStart = (clientX) => {
    dragStartX.current = clientX;
    isDragging.current = true;
  };

  const onDragEnd = (clientX) => {
    if (!isDragging.current || dragStartX.current === null) return;
    const delta = dragStartX.current - clientX;
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta > 0) {
        // Swiped left → next slide
        goTo((activeIdx + 1) % items.length);
      } else {
        // Swiped right → previous slide
        goTo((activeIdx - 1 + items.length) % items.length);
      }
    }
    dragStartX.current = null;
    isDragging.current = false;
  };

  // Touch handlers
  const handleTouchStart = (e) => onDragStart(e.touches[0].clientX);
  const handleTouchEnd   = (e) => onDragEnd(e.changedTouches[0].clientX);

  // Mouse handlers (desktop drag)
  const handleMouseDown = (e) => onDragStart(e.clientX);
  const handleMouseUp   = (e) => onDragEnd(e.clientX);
  const handleMouseLeave = () => { isDragging.current = false; dragStartX.current = null; };
  // ────────────────────────────────────────────────────────────────

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { hasPlayedOnceRef.current = hasPlayedOnce; }, [hasPlayedOnce]);

  // Fetch carousel items
  useEffect(() => {
    fetch(`${API}/api/carousel`)
      .then(r => r.json())
      .then(d => {
        if (d.success && d.items && d.items.length > 0) {
          setItems(d.items);
          setActiveIdx(0);
        }
      })
      .catch(() => {});
  }, [refreshTrigger]);

  // Measure container width
  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const ro = new ResizeObserver(() => { if (el) setContainerW(el.offsetWidth); });
    ro.observe(el);
    setContainerW(el.offsetWidth);
    return () => ro.disconnect();
  }, [items.length]);

  // Auto-scroll - only when muted and not playing
  useEffect(() => {
    if (items.length <= 1 || !muted || isPlaying) return;
    timerRef.current = setTimeout(() => {
      setActiveIdx(p => (p + 1) % items.length);
    }, AUTO_DELAY);
    return () => clearTimeout(timerRef.current);
  }, [activeIdx, items.length, muted, isPlaying]);

  // YouTube IFrame API - listen for video events
  useEffect(() => {
    const handler = (e) => {
      if (e.origin !== 'https://www.youtube.com') return;
      try {
        const d = JSON.parse(e.data);
        // State changes: -1=unstarted, 0=ended, 1=playing, 2=paused, 3=buffering, 5=cued
        if (d.event === 'onStateChange') {
          // When video ends (state 0)
          if (d.info === 0) {
            if (!mutedRef.current && hasPlayedOnceRef.current) {
              // Video finished one full play while unmuted, advance and reset
              setMuted(true);
              setHasPlayedOnce(false);
              setActiveIdx(p => (p + 1) % items.length);
            } else if (!mutedRef.current) {
              // First time ending while unmuted, mark as played once
              setHasPlayedOnce(true);
            }
          }
        }
      } catch {}
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [items.length]);

  // Send unmute command to active YouTube iframe
  useEffect(() => {
    if (!iframeRef.current) return;
    try {
      if (!muted) {
        // When unmuted: play with sound, disable loop
        iframeRef.current.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: 'unMute',
          args: []
        }), '*');
      } else {
        // When muted: mute and enable loop
        iframeRef.current.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: 'mute',
          args: []
        }), '*');
      }
    } catch {}
  }, [muted, activeIdx]);

  const goTo = (i) => {
    clearTimeout(timerRef.current);
    setMuted(true);
    setIsPlaying(false); // Stop playing when switching slides
    setActiveIdx(i);
  };

  const handleToggleMute = (e) => {
    e?.stopPropagation();
    setMuted(m => !m);
    if (muted) {
      // When unmuting, start playing and reset counter
      setIsPlaying(true);
      setHasPlayedOnce(false);
    }
  };

  const handleSlideClick = () => {
    // Start playing when user clicks on the slide
    if (!isPlaying) {
      setIsPlaying(true);
      setMuted(true); // Start muted
    }
  };

  const handleVideoEnded = () => {
    if (!muted && hasPlayedOnce) {
      // Advance after one full play
      setMuted(true);
      setHasPlayedOnce(false);
      setActiveIdx(p => (p + 1) % items.length);
    } else if (!muted) {
      // Mark first play complete
      setHasPlayedOnce(true);
    }
  };

  if (!items.length) return null;

  // ── Single item ────────────────────────────────────────────────────────────
  if (items.length === 1) {
    const item = items[0];
    const ytId = item.media_type === 'video' ? getYTId(item.media_url) : null;
    const tag = TAG_COLORS[item.tag_style] || TAG_COLORS.gold;
    
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 18px' }}>
        <div 
          onClick={handleSlideClick}
          style={{ width: CARD_W, height: CARD_H, borderRadius: 10, overflow: 'hidden', position: 'relative', boxShadow: '0 8px 28px rgba(0,0,0,0.65)', cursor: 'pointer' }}>
          {ytId ? (
            isPlaying ? (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <iframe
                  ref={iframeRef}
                  src={ytEmbed(ytId)}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    border: 0, 
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                  title={item.title}
                />
                <div 
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              </div>
            ) : (
              <img 
                src={ytThumb(ytId, item.thumbnail_url)}
                alt={item.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" decoding="async"/>
            )
          ) : item.media_type === 'video' ? (
            isPlaying ? (
              <video 
                src={item.media_url.startsWith('/api') ? `${API}${item.media_url}` : item.media_url}
                autoPlay 
                muted={muted} 
                loop={muted} 
                playsInline
                onEnded={!muted ? handleVideoEnded : undefined}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
              />
            ) : (
              <img 
                src={item.thumbnail_url && (item.thumbnail_url.startsWith('http://') || item.thumbnail_url.startsWith('https://')) ? 
                  item.thumbnail_url : 
                  item.media_url.startsWith('/api') ? `${API}${item.media_url}` : item.media_url}
                alt={item.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" decoding="async"/>
            )
          ) : (
            <img 
              src={item.media_url.startsWith('/api') ? `${API}${item.media_url}` : item.media_url}
              alt={item.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" decoding="async"/>
          )}

          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 56, background: 'linear-gradient(to top, #111827, transparent)', pointerEvents: 'none' }} />

          {item.tag_label && (
            <div style={{ position: 'absolute', top: 8, left: 8, background: tag.bg, color: tag.color, fontSize: 9, fontWeight: 800, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {item.tag_label.slice(0, 8)}
            </div>
          )}

          {isPlaying && item.media_type === 'video' && (
            <button 
              onClick={handleToggleMute}
              style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
              {muted ? <VolumeX size={16} color="#fff" /> : <Volume2 size={16} color="#fff" />}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Multi-item carousel ────────────────────────────────────────────────────
  const trackX = containerW / 2 - CARD_W / 2 - activeIdx * (CARD_W + GAP);

  return (
    <div>
      <div
        ref={wrapRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{
          overflow: 'hidden',
          padding: `${CARD_H * 0.05}px 0 ${CARD_H * 0.08}px`,
          cursor: isDragging.current ? 'grabbing' : 'grab',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <div style={{
          display: 'flex', 
          gap: GAP,
          transform: `translateX(${trackX}px)`,
          transition: `transform ${EASE}ms cubic-bezier(0.4,0,0.2,1)`,
          willChange: 'transform',
        }}>
          {items.map((item, i) => {
            const isActive = i === activeIdx;
            const ytId = item.media_type === 'video' ? getYTId(item.media_url) : null;
            const tag = TAG_COLORS[item.tag_style] || TAG_COLORS.gold;

            return (
              <div
                key={item.id}
                onClick={() => {
                  // Only handle as click if it wasn't a swipe
                  if (Math.abs((dragStartX.current || 0) - 0) < SWIPE_THRESHOLD) {
                    if (!isActive) {
                      goTo(i);
                    } else {
                      handleSlideClick();
                    }
                  }
                }}
                style={{
                  width: CARD_W, 
                  height: CARD_H, 
                  flexShrink: 0,
                  borderRadius: 10, 
                  overflow: 'hidden', 
                  position: 'relative', 
                  cursor: 'pointer',
                  transform: isActive ? 'scale(1.05)' : 'scale(0.92)',
                  transition: `transform ${EASE}ms cubic-bezier(0.4,0,0.2,1), box-shadow ${EASE}ms ease`,
                  boxShadow: isActive ? '0 8px 28px rgba(0,0,0,0.65)' : '0 4px 16px rgba(0,0,0,0.4)',
                }}
              >
                {ytId ? (
                  isActive && isPlaying ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      <iframe
                        ref={iframeRef}
                        src={ytEmbed(ytId)}
                        allow="autoplay; fullscreen"
                        allowFullScreen
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          border: 0, 
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                        title={item.title}
                      />
                      <div 
                        style={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          pointerEvents: 'none',
                          zIndex: 1
                        }}
                      />
                    </div>
                  ) : (
                    <img 
                      src={ytThumb(ytId, item.thumbnail_url)}
                      alt={item.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" decoding="async"/>
                  )
                ) : item.media_type === 'video' ? (
                  isActive && isPlaying ? (
                    <video 
                      src={item.media_url.startsWith('/api') ? `${API}${item.media_url}` : item.media_url}
                      autoPlay 
                      muted={muted}
                      loop={muted}
                      playsInline
                      onEnded={!muted && hasPlayedOnce ? handleVideoEnded : undefined}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover', 
                        display: 'block'
                      }} 
                    />
                  ) : (
                    <img 
                      src={item.thumbnail_url && (item.thumbnail_url.startsWith('http://') || item.thumbnail_url.startsWith('https://')) ? 
                        item.thumbnail_url : 
                        item.media_url.startsWith('/api') ? `${API}${item.media_url}` : item.media_url}
                      alt={item.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" decoding="async"/>
                  )
                ) : (
                  <img 
                    src={item.media_url.startsWith('/api') ? `${API}${item.media_url}` : item.media_url}
                    alt={item.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" decoding="async"/>
                )}

                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 56, background: 'linear-gradient(to top, #111827, transparent)', pointerEvents: 'none' }} />

                {item.tag_label && (
                  <div style={{ position: 'absolute', top: 8, left: 8, background: tag.bg, color: tag.color, fontSize: 9, fontWeight: 800, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {item.tag_label.slice(0, 8)}
                  </div>
                )}

                {isActive && isPlaying && item.media_type === 'video' && (
                  <button 
                    onClick={handleToggleMute}
                    style={{ 
                      position: 'absolute', 
                      bottom: 8, 
                      left: 8, 
                      zIndex: 10, 
                      background: 'rgba(0,0,0,0.6)', 
                      border: 'none', 
                      cursor: 'pointer', 
                      padding: 6, 
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    {muted ? <VolumeX size={16} color="#fff" /> : <Volume2 size={16} color="#fff" />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10 }}>
        {items.map((_, i) => (
          <div 
            key={i} 
            onClick={() => goTo(i)}
            style={{ 
              cursor: 'pointer', 
              width: i === activeIdx ? 20 : 6, 
              height: 6, 
              borderRadius: 3, 
              background: i === activeIdx ? '#e8b84b' : 'rgba(255,255,255,0.25)', 
              transition: 'all 300ms ease' 
            }} 
          />
        ))}
      </div>
    </div>
  );
}
